import * as XLSX from 'xlsx';

/**
 * Excel数据加载器
 * 用于读取和解析 x1_x3_priority_results.xlsx、基期四象限.xlsx 和 SSP_accessibility_disparity.xlsx 文件
 * 读取真实的priority数据、quadrant数据和accessibility/disparity数据
 */
class ExcelDataLoader {
  constructor() {
    this.data = null;
    this.cityDataMap = new Map(); // 存储城市数据：cityCode_scenario -> 数据对象
    this.quadrantDataMap = new Map(); // 存储象限数据：cityCode_year -> quadrant值
    this.sspQuadrantDataMap = new Map(); // 存储SSP象限数据：cityCode_year_scenario -> quadrant值
    this.accessibilityDataMap = new Map(); // 存储accessibility/disparity数据：cityCode -> {SSP1_accessibility, SSP1_disparity, ...}
    this.yearAccessibilityDataMap = new Map(); // 存储基期accessibility/disparity数据：cityCode_year -> {accessibility, disparity}
    this.wellbeingDataMap = new Map(); // 存储wellbeing数据：cityCode_year -> wellbeing值
    this.isLoaded = false;
    this.isQuadrantLoaded = false;
    this.isSspQuadrantLoaded = false;
    this.isAccessibilityLoaded = false;
    this.isYearAccessibilityLoaded = false;
    this.isWellbeingLoaded = false;
  }

  /**
   * 加载Excel文件数据
   * @returns {Promise<boolean>} 加载是否成功
   */
  async loadData() {
    try {
      console.log('开始加载Excel文件...');
      
      // 并行加载六个Excel文件
      const [prioritySuccess, quadrantSuccess, sspQuadrantSuccess, accessibilitySuccess, yearAccessibilitySuccess, wellbeingSuccess] = await Promise.all([
        this.loadPriorityData(),
        this.loadQuadrantData(),
        this.loadSSPQuadrantData(),
        this.loadAccessibilityData(),
        this.loadYearAccessibilityData(),
        this.loadWellbeingData()
      ]);
      
      this.isLoaded = prioritySuccess && quadrantSuccess && sspQuadrantSuccess && accessibilitySuccess && yearAccessibilitySuccess && wellbeingSuccess;
      return this.isLoaded;
      
    } catch (error) {
      console.error('加载Excel文件失败:', error);
      this.isLoaded = false;
      return false;
    }
  }

  /**
   * 加载priority数据
   * @returns {Promise<boolean>}
   */
  async loadPriorityData() {
    try {
      // 从public/data/excel目录读取文件
      const response = await fetch('/data/excel/x1_x3_priority_results.xlsx');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // 获取第一个工作表
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // 将工作表转换为JSON数组，保留标题行
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      console.log('Priority Excel文件读取成功，数据行数:', jsonData.length);
      
      // 处理数据
      this.processPriorityData(jsonData);
      
      return true;
      
    } catch (error) {
      console.error('加载Priority Excel文件失败:', error);
      return false;
    }
  }

  /**
   * 加载象限数据
   * @returns {Promise<boolean>}
   */
  async loadQuadrantData() {
    try {
      // 从public/data/excel目录读取基期四象限.xlsx文件
      const response = await fetch('/data/excel/基期四象限.xlsx');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // 获取第一个工作表
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // 将工作表转换为JSON数组，保留标题行
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      console.log('Quadrant Excel文件读取成功，数据行数:', jsonData.length);
      
      // 处理象限数据
      this.processQuadrantData(jsonData);
      
      this.isQuadrantLoaded = true;
      return true;
      
    } catch (error) {
      console.error('加载Quadrant Excel文件失败:', error);
      this.isQuadrantLoaded = false;
      return false;
    }
  }

  /**
   * 加载SSP象限数据
   * @returns {Promise<boolean>}
   */
  async loadSSPQuadrantData() {
    try {
      // 从public/data/excel目录读取SSP四象限.xlsx文件
      const response = await fetch('/data/excel/SSP四象限.xlsx');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // 获取第一个工作表
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // 将工作表转换为JSON数组，保留标题行
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      console.log('SSP Quadrant Excel文件读取成功，数据行数:', jsonData.length);
      
      // 处理SSP象限数据
      this.processSSPQuadrantData(jsonData);
      
      this.isSspQuadrantLoaded = true;
      return true;
      
    } catch (error) {
      console.error('加载SSP Quadrant Excel文件失败:', error);
      this.isSspQuadrantLoaded = false;
      return false;
    }
  }

  /**
   * 加载accessibility和disparity数据
   * @returns {Promise<boolean>}
   */
  async loadAccessibilityData() {
    try {
      // 从public/data/excel目录读取SSP_accessibility_disparity.xlsx文件
      const response = await fetch('/data/excel/SSP_accessibility_disparity.xlsx');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // 获取第一个工作表
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // 将工作表转换为JSON数组，保留标题行
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      console.log('Accessibility Excel文件读取成功，数据行数:', jsonData.length);
      
      // 处理accessibility数据
      this.processAccessibilityData(jsonData);
      
      this.isAccessibilityLoaded = true;
      return true;
      
    } catch (error) {
      console.error('加载Accessibility Excel文件失败:', error);
      this.isAccessibilityLoaded = false;
      return false;
    }
  }

  /**
   * 加载基期accessibility和disparity数据
   * @returns {Promise<boolean>}
   */
  async loadYearAccessibilityData() {
    try {
      // 从public/data/excel目录读取基期accessibility_disparity.xlsx文件
      const response = await fetch('/data/excel/基期accessibility_disparity.xlsx');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // 获取第一个工作表
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // 将工作表转换为JSON数组，保留标题行
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      console.log('Year Accessibility Excel文件读取成功，数据行数:', jsonData.length);
      
      // 处理基期accessibility数据
      this.processYearAccessibilityData(jsonData);
      
      this.isYearAccessibilityLoaded = true;
      return true;
      
    } catch (error) {
      console.error('加载Year Accessibility Excel文件失败:', error);
      this.isYearAccessibilityLoaded = false;
      return false;
    }
  }

  /**
   * 加载wellbeing数据
   * @returns {Promise<boolean>}
   */
  async loadWellbeingData() {
    try {
      // 从public/data/excel目录读取wellbeing.xlsx文件
      const response = await fetch('/data/excel/wellbeing.xlsx');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // 获取第一个工作表
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      // 将工作表转换为JSON数组，保留标题行
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      console.log('Wellbeing Excel文件读取成功，数据行数:', jsonData.length);
      
      // 处理wellbeing数据
      this.processWellbeingData(jsonData);
      
      this.isWellbeingLoaded = true;
      return true;
      
    } catch (error) {
      console.error('加载Wellbeing Excel文件失败:', error);
      this.isWellbeingLoaded = false;
      return false;
    }
  }

  /**
   * 处理Priority Excel数据，提取所有需要的列
   * @param {Array} rawData - 原始Excel数据
   */
  processPriorityData(rawData) {
    // 清空之前的数据
    this.cityDataMap.clear();
    
    if (rawData.length < 2) {
      console.error('Priority Excel文件数据不足');
      return;
    }
    
    // 获取标题行，确定列的位置
    const headers = rawData[0];
    const columnMap = this.getColumnMapping(headers);
    
    console.log('Priority列映射:', columnMap);
    
    // 跳过标题行，从第二行开始处理
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      
      if (row && row.length > 0) {
        const cityData = this.extractRowData(row, columnMap);
        
        if (cityData && cityData.code && cityData.scenario) {
          // 使用 cityCode_scenario 作为键
          const key = `${cityData.code}_${cityData.scenario}`;
          this.cityDataMap.set(key, cityData);
        }
      }
    }
    
    console.log('Priority数据处理完成，记录数量:', this.cityDataMap.size);
  }

  /**
   * 处理象限数据
   * @param {Array} rawData - 原始Excel数据
   */
  processQuadrantData(rawData) {
    // 清空之前的象限数据
    this.quadrantDataMap.clear();
    
    if (rawData.length < 2) {
      console.error('Quadrant Excel文件数据不足');
      return;
    }
    
    console.log('Quadrant标题行:', rawData[0]);
    
    // 跳过标题行，从第二行开始处理
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      
      if (row && row.length >= 4) {
        const year = row[0]; // 第一列
        const code = row[1]; // 第二列
        const quadrant = row[3]; // 第四列
        
        if (year && code && quadrant) {
          // 确保城市编码是6位数字字符串
          const cityCode = String(code).padStart(6, '0');
          const yearStr = String(year);
          
          // 使用 cityCode_year 作为键
          const key = `${cityCode}_${yearStr}`;
          this.quadrantDataMap.set(key, parseInt(quadrant));
        }
      }
    }
    
    console.log('Quadrant数据处理完成，记录数量:', this.quadrantDataMap.size);
    console.log('前5条象限数据:', Array.from(this.quadrantDataMap.entries()).slice(0, 5));
  }

  /**
   * 处理SSP象限数据
   * @param {Array} rawData - 原始Excel数据
   */
  processSSPQuadrantData(rawData) {
    // 清空之前的SSP象限数据
    this.sspQuadrantDataMap.clear();
    
    if (rawData.length < 2) {
      console.error('SSP Quadrant Excel文件数据不足');
      return;
    }
    
    console.log('SSP Quadrant标题行:', rawData[0]);
    
    // 跳过标题行，从第二行开始处理
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      
      if (row && row.length >= 8) {
        const code = row[1]; // 第二列：code
        const year = row[2]; // 第三列：year
        const ssp1_quadrant = row[3]; // 第四列：SSP1_quadrant
        const ssp2_quadrant = row[4]; // 第五列：SSP2_quadrant
        const ssp3_quadrant = row[5]; // 第六列：SSP3_quadrant
        const ssp4_quadrant = row[6]; // 第七列：SSP4_quadrant
        const ssp5_quadrant = row[7]; // 第八列：SSP5_quadrant
        
        if (code && year) {
          // 确保城市编码是6位数字字符串
          const cityCode = String(code).padStart(6, '0');
          const yearStr = String(year);
          
          // 存储每个SSP的象限数据
          const sspQuadrants = {
            SSP1: parseInt(ssp1_quadrant),
            SSP2: parseInt(ssp2_quadrant),
            SSP3: parseInt(ssp3_quadrant),
            SSP4: parseInt(ssp4_quadrant),
            SSP5: parseInt(ssp5_quadrant)
          };
          
          // 为每个SSP存储数据
          Object.entries(sspQuadrants).forEach(([ssp, quadrant]) => {
            if (!isNaN(quadrant)) {
              const key = `${cityCode}_${yearStr}_${ssp}`;
              this.sspQuadrantDataMap.set(key, quadrant);
            }
          });
        }
      }
    }
    
    console.log('SSP Quadrant数据处理完成，记录数量:', this.sspQuadrantDataMap.size);
  }

  /**
   * 处理accessibility数据
   * @param {Array} rawData - 原始Excel数据
   */
  processAccessibilityData(rawData) {
    // 清空之前的accessibility数据
    this.accessibilityDataMap.clear();
    
    if (rawData.length < 2) {
      console.error('Accessibility Excel文件数据不足');
      return;
    }
    
    console.log('Accessibility标题行:', rawData[0]);
    
    // 跳过标题行，从第二行开始处理
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      
      if (row && row.length >= 13) {
        const year = row[1]; // 第二列：year
        const code = row[12]; // 第13列（索引12）：code
        
        if (code && year) {
          // 确保城市编码是6位数字字符串
          const cityCode = String(code).padStart(6, '0');
          const yearStr = String(year);
          
          // 提取第3-12列的数据（索引2-11）
          const accessibilityData = {
            SSP1_accessibility: parseFloat(row[2]) || 0,
            SSP1_disparity: parseFloat(row[3]) || 0,
            SSP2_accessibility: parseFloat(row[4]) || 0,
            SSP2_disparity: parseFloat(row[5]) || 0,
            SSP3_accessibility: parseFloat(row[6]) || 0,
            SSP3_disparity: parseFloat(row[7]) || 0,
            SSP4_accessibility: parseFloat(row[8]) || 0,
            SSP4_disparity: parseFloat(row[9]) || 0,
            SSP5_accessibility: parseFloat(row[10]) || 0,
            SSP5_disparity: parseFloat(row[11]) || 0
          };
          
          // 使用 cityCode_year 作为键
          const key = `${cityCode}_${yearStr}`;
          this.accessibilityDataMap.set(key, accessibilityData);
        }
      }
    }
    
    console.log('Accessibility数据处理完成，记录数量:', this.accessibilityDataMap.size);
    console.log('前5条accessibility数据:', Array.from(this.accessibilityDataMap.entries()).slice(0, 5));
  }

  /**
   * 处理基期accessibility数据
   * @param {Array} rawData - 原始Excel数据
   */
  processYearAccessibilityData(rawData) {
    // 清空之前的基期accessibility数据
    this.yearAccessibilityDataMap.clear();
    
    if (rawData.length < 2) {
      console.error('Year Accessibility Excel文件数据不足');
      return;
    }
    
    console.log('Year Accessibility标题行:', rawData[0]);
    
    // 跳过标题行，从第二行开始处理
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      
      if (row && row.length >= 5) {
        const accessibility = row[1]; // 第2列
        const year = row[2]; // 第3列
        const disparity = row[3]; // 第4列
        const code = row[4]; // 第5列
        
        if (year && code && accessibility !== undefined && disparity !== undefined) {
          // 确保城市编码是6位数字字符串
          const cityCode = String(code).padStart(6, '0');
          const yearStr = String(year);
          
          // 使用 cityCode_year 作为键
          const key = `${cityCode}_${yearStr}`;
          this.yearAccessibilityDataMap.set(key, {
            accessibility: parseFloat(accessibility) || 0,
            disparity: parseFloat(disparity) || 0
          });
        }
      }
    }
    
    console.log('Year Accessibility数据处理完成，记录数量:', this.yearAccessibilityDataMap.size);
    console.log('前5条年份accessibility数据:', Array.from(this.yearAccessibilityDataMap.entries()).slice(0, 5));
  }

  /**
   * 处理wellbeing数据
   * @param {Array} rawData 原始数据
   */
  processWellbeingData(rawData) {
    if (!rawData || rawData.length < 2) {
      console.error('Wellbeing数据为空或格式不正确');
      return;
    }
    
    // 获取表头
    const headers = rawData[0];
    
    // 确定列映射
    const yearIndex = 0;  // 第一列是年份
    const cityCodeIndex = 1;  // 第二列是citycode
    const wellbeingIndex = 3;  // 第四列是wellbeing值
    
    // 处理数据行
    for (let i = 1; i < rawData.length; i++) {
      const row = rawData[i];
      if (row.length <= Math.max(yearIndex, cityCodeIndex, wellbeingIndex)) {
        continue;  // 跳过不完整的行
      }
      
      const year = String(row[yearIndex]);
      const cityCode = String(row[cityCodeIndex]);
      const wellbeing = row[wellbeingIndex];
      
      if (!cityCode || !year || wellbeing === undefined) {
        continue;  // 跳过缺少关键数据的行
      }
      
      // 存储数据：cityCode_year -> wellbeing值
      const key = `${cityCode}_${year}`;
      this.wellbeingDataMap.set(key, wellbeing);
    }
    
    console.log(`成功处理 ${this.wellbeingDataMap.size} 条Wellbeing数据`);
  }

  /**
   * 根据标题行确定列的位置
   * @param {Array} headers - 标题行数组
   * @returns {Object} 列名到索引的映射
   */
  getColumnMapping(headers) {
    const mapping = {};
    
    headers.forEach((header, index) => {
      const headerStr = String(header).toLowerCase().trim();
      
      if (headerStr.includes('code')) {
        mapping.code = index;
      } else if (headerStr.includes('scenario')) {
        mapping.scenario = index;
      } else if (headerStr.includes('top_variable')) {
        mapping.top_variable = index;
      } else if (headerStr.includes('x2_urarea_synergy')) {
        mapping.x2_urArea_synergy = index;
      } else if (headerStr.includes('urban_pop_synergy')) {
        mapping.urban_pop_synergy = index;
      } else if (headerStr.includes('over_thre_synergy')) {
        mapping.over_thre_synergy = index;
      } else if (headerStr.includes('mesh_all_synergy')) {
        mapping.MESH_All_synergy = index;
      }
    });
    
    return mapping;
  }

  /**
   * 从行数据中提取城市信息
   * @param {Array} row - 行数据
   * @param {Object} columnMap - 列映射
   * @returns {Object|null} 城市数据对象
   */
  extractRowData(row, columnMap) {
    try {
      const code = row[columnMap.code];
      const scenario = row[columnMap.scenario];
      const top_variable = row[columnMap.top_variable];
      const x2_urArea_synergy = row[columnMap.x2_urArea_synergy];
      const urban_pop_synergy = row[columnMap.urban_pop_synergy];
      const over_thre_synergy = row[columnMap.over_thre_synergy];
      const MESH_All_synergy = row[columnMap.MESH_All_synergy];
      
      if (!code || !scenario || !top_variable) {
        return null;
      }
      
      // 确保城市编码是6位数字字符串
      const cityCode = String(code).padStart(6, '0');
      
      // 构建synergy数据对象
      const synergyData = {
        x2_urArea: parseFloat(x2_urArea_synergy) || 0,
        urban_pop: parseFloat(urban_pop_synergy) || 0,
        over_thre: parseFloat(over_thre_synergy) || 0,
        MESH_All: parseFloat(MESH_All_synergy) || 0
      };
      
      // 确定top_variable对应的方向（正值为positive，负值为negative）
      const topVariableValue = synergyData[top_variable];
      const direction = topVariableValue >= 0 ? 'positive' : 'negative';
      
      return {
        code: cityCode,
        scenario: String(scenario),
        priorityValue: String(top_variable),
        priorityDirection: direction,
        synergyData: synergyData,
        topVariableValue: topVariableValue
      };
      
    } catch (error) {
      console.error('提取行数据失败:', error, row);
      return null;
    }
  }

  /**
   * 根据城市编码和scenario获取priority数据
   * @param {string} cityCode - 6位城市编码
   * @param {string} scenario - scenario值，默认为'SSP2'
   * @returns {Object|null} 城市priority数据
   */
  getCityPriorityData(cityCode, scenario = 'SSP2') {
    if (!this.isLoaded) {
      console.warn('Excel数据尚未加载，请先调用loadData()');
      return null;
    }
    
    // 确保城市编码格式正确
    const formattedCode = String(cityCode).padStart(6, '0');
    const key = `${formattedCode}_${scenario}`;
    
    return this.cityDataMap.get(key) || null;
  }

  /**
   * 获取所有城市的priority数据，按scenario分组
   * @returns {Object} scenario -> cityCode -> 数据的映射
   */
  getAllPriorityData() {
    if (!this.isLoaded) {
      console.warn('Excel数据尚未加载，请先调用loadData()');
      return {};
    }
    
    const result = {};
    
    for (const [key, data] of this.cityDataMap) {
      const scenario = data.scenario;
      const cityCode = data.code;
      
      if (!result[scenario]) {
        result[scenario] = {};
      }
      
      result[scenario][cityCode] = data;
    }
    
    return result;
  }

  /**
   * 获取特定scenario下的所有城市数据
   * @param {string} scenario - scenario值
   * @returns {Object} cityCode -> 数据的映射
   */
  getScenarioData(scenario) {
    if (!this.isLoaded) {
      console.warn('Excel数据尚未加载，请先调用loadData()');
      return {};
    }
    
    const result = {};
    
    for (const [key, data] of this.cityDataMap) {
      if (data.scenario === scenario) {
        result[data.code] = data;
      }
    }
    
    return result;
  }

  /**
   * 检查数据是否已加载
   * @returns {boolean}
   */
  isDataLoaded() {
    return this.isLoaded;
  }

  /**
   * 获取加载的记录数量
   * @returns {number}
   */
  getRecordCount() {
    return this.cityDataMap.size;
  }

  /**
   * 获取所有可用的scenario列表
   * @returns {Array} scenario列表
   */
  getAvailableScenarios() {
    const scenarios = new Set();
    for (const data of this.cityDataMap.values()) {
      scenarios.add(data.scenario);
    }
    return Array.from(scenarios);
  }

  /**
   * 检查特定城市和scenario是否存在数据
   * @param {string} cityCode - 城市编码
   * @param {string} scenario - scenario值
   * @returns {boolean}
   */
  hasCityData(cityCode, scenario = 'SSP2') {
    const formattedCode = String(cityCode).padStart(6, '0');
    const key = `${formattedCode}_${scenario}`;
    return this.cityDataMap.has(key);
  }

  /**
   * 获取城市的象限数据
   * @param {string} cityCode - 6位城市编码
   * @param {string} year - 年份
   * @returns {number|null} 象限值(1-4)
   */
  getCityQuadrant(cityCode, year) {
    if (!this.isQuadrantLoaded) {
      console.warn('象限数据尚未加载');
      return null;
    }
    
    // 确保城市编码格式正确
    const formattedCode = String(cityCode).padStart(6, '0');
    const key = `${formattedCode}_${year}`;
    
    return this.quadrantDataMap.get(key) || null;
  }

  /**
   * 象限值转换为分类代码
   * @param {number} quadrant - 象限值(1-4)
   * @returns {string} 分类代码(LL/HL/LH/HH)
   */
  quadrantToCategory(quadrant) {
    switch(quadrant) {
      case 1: return 'LL';
      case 2: return 'HL';
      case 3: return 'LH';
      case 4: return 'HH';
      default: return 'LL';
    }
  }

  /**
   * 获取特定年份下的所有城市象限数据
   * @param {string} year - 年份
   * @returns {Object} cityCode -> quadrant的映射
   */
  getYearQuadrantData(year) {
    if (!this.isQuadrantLoaded) {
      console.warn('象限数据尚未加载');
      return {};
    }
    
    const result = {};
    
    for (const [key, quadrant] of this.quadrantDataMap) {
      if (key.endsWith(`_${year}`)) {
        const cityCode = key.split('_')[0];
        result[cityCode] = quadrant;
      }
    }
    
    return result;
  }

  /**
   * 检查象限数据是否已加载
   * @returns {boolean}
   */
  isQuadrantDataLoaded() {
    return this.isQuadrantLoaded;
  }

  /**
   * 获取所有可用的年份列表
   * @returns {Array} 年份列表
   */
  getAvailableYears() {
    const years = new Set();
    for (const key of this.quadrantDataMap.keys()) {
      const year = key.split('_')[1];
      years.add(year);
    }
    return Array.from(years).sort();
  }

  /**
   * 获取城市的accessibility和disparity数据
   * @param {string} cityCode - 6位城市编码
   * @param {string} scenario - SSP场景
   * @param {string} year - 年份（可选）
   * @returns {Object|null} {accessibility: number, disparity: number}
   */
  getCityAccessibilityData(cityCode, scenario, year = null) {
    if (!this.isAccessibilityLoaded) {
      console.warn('Accessibility数据尚未加载');
      return null;
    }
    
    // 确保城市编码格式正确
    const formattedCode = String(cityCode).padStart(6, '0');
    
    let data;
    if (year) {
      // 如果提供了年份，使用年份相关的键
      const key = `${formattedCode}_${year}`;
      data = this.accessibilityDataMap.get(key);
    } else {
      // 兼容旧的逻辑（没有年份）
      data = this.accessibilityDataMap.get(formattedCode);
    }
    
    if (!data) {
      return null;
    }
    
    const accessibilityKey = `${scenario}_accessibility`;
    const disparityKey = `${scenario}_disparity`;
    
    return {
      accessibility: data[accessibilityKey] || 0,
      disparity: data[disparityKey] || 0
    };
  }

  /**
   * 检查accessibility数据是否已加载
   * @returns {boolean}
   */
  isAccessibilityDataLoaded() {
    return this.isAccessibilityLoaded;
  }

  /**
   * 获取城市在特定年份的accessibility和disparity数据
   * @param {string} cityCode - 6位城市编码
   * @param {string} year - 年份
   * @returns {Object|null} {accessibility: number, disparity: number}
   */
  getCityYearAccessibilityData(cityCode, year) {
    if (!this.isYearAccessibilityLoaded) {
      console.warn('年份Accessibility数据尚未加载');
      return null;
    }
    
    // 确保城市编码格式正确
    const formattedCode = String(cityCode).padStart(6, '0');
    const key = `${formattedCode}_${year}`;
    
    return this.yearAccessibilityDataMap.get(key) || null;
  }

  /**
   * 检查年份accessibility数据是否已加载
   * @returns {boolean}
   */
  isYearAccessibilityDataLoaded() {
    return this.isYearAccessibilityLoaded;
  }

  /**
   * 获取特定年份和SSP下的所有城市象限数据
   * @param {string} year - 年份
   * @param {string} scenario - SSP场景
   * @returns {Object} cityCode -> quadrant的映射
   */
  getYearSSPQuadrantData(year, scenario) {
    if (!this.isSspQuadrantLoaded) {
      console.warn('SSP象限数据尚未加载');
      return {};
    }
    
    const result = {};
    
    for (const [key, quadrant] of this.sspQuadrantDataMap) {
      if (key.includes(`_${year}_${scenario}`)) {
        const cityCode = key.split('_')[0];
        result[cityCode] = quadrant;
      }
    }
    
    return result;
  }

  /**
   * 获取城市某年的wellbeing值
   * @param {string} cityCode 城市代码
   * @param {string} year 年份
   * @returns {number|null} wellbeing值，如果不存在则返回null
   */
  getCityWellbeing(cityCode, year) {
    const key = `${cityCode}_${year}`;
    return this.wellbeingDataMap.has(key) ? this.wellbeingDataMap.get(key) : null;
  }

  /**
   * 检查wellbeing数据是否已加载
   * @returns {boolean}
   */
  isWellbeingDataLoaded() {
    return this.isWellbeingLoaded;
  }
}

// 创建单例实例
const excelDataLoader = new ExcelDataLoader();

// 导出实例和类
export default excelDataLoader;
export { ExcelDataLoader };

/**
 * 便捷函数：获取城市的priority数据
 * @param {string} cityCode - 城市编码
 * @param {string} scenario - scenario值
 * @returns {Object|null}
 */
export const getCityPriorityData = (cityCode, scenario = 'SSP2') => {
  return excelDataLoader.getCityPriorityData(cityCode, scenario);
};

/**
 * 便捷函数：获取特定scenario的所有数据
 * @param {string} scenario - scenario值
 * @returns {Object}
 */
export const getScenarioData = (scenario) => {
  return excelDataLoader.getScenarioData(scenario);
};

/**
 * 便捷函数：初始化Excel数据加载
 * @returns {Promise<boolean>}
 */
export const initializeExcelData = async () => {
  return await excelDataLoader.loadData();
};

/**
 * 便捷函数：获取城市的象限数据
 * @param {string} cityCode - 城市编码
 * @param {string} year - 年份
 * @returns {number|null}
 */
export const getCityQuadrant = (cityCode, year) => {
  return excelDataLoader.getCityQuadrant(cityCode, year);
};

/**
 * 便捷函数：获取特定年份的所有象限数据
 * @param {string} year - 年份
 * @returns {Object}
 */
export const getYearQuadrantData = (year) => {
  return excelDataLoader.getYearQuadrantData(year);
};

/**
 * 便捷函数：象限值转换为分类代码
 * @param {number} quadrant - 象限值
 * @returns {string}
 */
export const quadrantToCategory = (quadrant) => {
  return excelDataLoader.quadrantToCategory(quadrant);
};

/**
 * 便捷函数：获取城市的accessibility和disparity数据
 * @param {string} cityCode - 城市编码
 * @param {string} scenario - SSP场景
 * @param {string} year - 年份（可选）
 * @returns {Object|null}
 */
export const getCityAccessibilityData = (cityCode, scenario, year = null) => {
  return excelDataLoader.getCityAccessibilityData(cityCode, scenario, year);
};

/**
 * 便捷函数：获取城市在特定年份的accessibility和disparity数据
 * @param {string} cityCode - 6位城市编码
 * @param {string} year - 年份
 * @returns {Object|null} {accessibility: number, disparity: number}
 */
export const getCityYearAccessibilityData = (cityCode, year) => {
  return excelDataLoader.getCityYearAccessibilityData(cityCode, year);
};

/**
 * 便捷函数：获取特定年份和SSP下的所有城市象限数据
 * @param {string} year - 年份
 * @param {string} scenario - SSP场景
 * @returns {Object} cityCode -> quadrant的映射
 */
export const getYearSSPQuadrantData = (year, scenario) => {
  return excelDataLoader.getYearSSPQuadrantData(year, scenario);
};

/**
 * 便捷函数：获取城市的wellbeing值
 * @param {string} cityCode 城市代码
 * @param {string} year 年份
 * @returns {number|null} wellbeing值，如果不存在则返回null
 */
export const getCityWellbeing = (cityCode, year) => {
  return excelDataLoader.getCityWellbeing(cityCode, year);
}; 